import { store } from '../../../store'
import { BroadSignFiltersModel } from '../../../api/broadSign/models/broad-sign-filters.model'
import { broadSignActions } from '../store/broad-sign-slice'
import api from '../../../api'
import { NotifierType } from '../../../components/Notifier'
import VariableUtils from '../../../utils/variable'
import { Company } from '../../../api/companies/types'
import { User } from '../../../types/user'
import { userFullName } from '../../../utils/user'
import { CampaignDepartment } from '../../../api/campaign/types'
import { BROADSIGN_CREATIONS_PER_PAGE } from '../../../constant'
import { BroadSignCreationMediumUsage } from '../../../api/broadSign/models/broad-sign-media-tree.model'

export default class BroadSignService {
  static fetchBroadSignCreations = (
    filters: BroadSignFiltersModel,
    pageNumber: number
  ): Promise<unknown> => {
    return Promise.resolve(store.dispatch(broadSignActions.setPageNumber(pageNumber)))
      .then(() => Promise.resolve(store.dispatch(broadSignActions.setLoading(true))))
      .then(() => {
        const pageToFirst = pageNumber * BROADSIGN_CREATIONS_PER_PAGE

        return api.broadSign.fetchBroadSignCreations(filters, pageToFirst)
      })
      .then(response => {
        if (!response.data?.broadsignCreations) {
          return
        }

        const broadsignCreations = response.data.broadsignCreations.nodes.map(creation => {
          return {
            ...creation,
            advertiser: creation.advertiser
              ? VariableUtils.toSelectAsyncValue<Company>({
                  value: creation.advertiser.name,
                  rawData: creation.advertiser,
                })
              : null,
            commercialSupervisor: creation.commercialSupervisor
              ? VariableUtils.toSelectAsyncValue<User>({
                  label: userFullName(creation.commercialSupervisor),
                  value: creation.commercialSupervisor.id,
                  rawData: creation.commercialSupervisor,
                })
              : null,
            department: creation.department
              ? VariableUtils.toSelectAsyncValue<CampaignDepartment>({
                  value: creation.department.name,
                  rawData: creation.department,
                })
              : null,
          }
        })

        return store.dispatch(
          broadSignActions.setData({
            data: [...broadsignCreations],
            totalItemsCount: response.data.broadsignCreations.totalCount,
          })
        )
      })
      .catch(() => [])
      .finally(() => void store.dispatch(broadSignActions.setLoading(false)))
  }

  static fetchCreationMediaTree = (id: string): Promise<unknown> => {
    return Promise.resolve(store.dispatch(broadSignActions.setMediaTreeLoading(true)))
      .then(() => api.broadSign.fetchBroadsignCreationMediaTree(id))
      .then(response =>
        store.dispatch(
          broadSignActions.setMediaTree(response.data?.broadsignCreationMediaTree ?? [])
        )
      )
      .catch(() => store.dispatch(broadSignActions.setMediaTree([])))
      .finally(() => void store.dispatch(broadSignActions.setMediaTreeLoading(false)))
  }

  static fetchCreationAcceptances = (id: string): Promise<unknown> => {
    return api.broadSign
      .fetchCreationAcceptances(id)
      .then(response =>
        store.dispatch(
          broadSignActions.setAcceptances(response.data?.broadsignCreationAcceptances ?? [])
        )
      )
      .catch(() => store.dispatch(broadSignActions.setAcceptances([])))
  }

  static assignCreationMedia = (
    id: string,
    media: BroadSignCreationMediumUsage[],
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string
  ): Promise<unknown> => {
    return (
      Promise.resolve(store.dispatch(broadSignActions.setMediaTreeLoading(true)))
        .then(() => api.broadSign.assignCreationMedia(id, media))
        .then(response => {
          const result = response.data?.assignBroadsignCreationMedia

          if (!result?.success) {
            addNotification(
              NotifierType.ERROR,
              result?.errors.length ? result.errors : t('broadSign.mediaAssignmentFailed')
            )

            return
          }

          addNotification(NotifierType.SUCCESS, t('broadSign.mediaAssignmentUpdated'))
        })
        .catch(() => addNotification(NotifierType.ERROR, t('broadSign.mediaAssignmentFailed')))
        // the tree decides which table a gallery lands in, so it has to be reloaded either way
        .finally(() => BroadSignService.fetchCreationMediaTree(id))
    )
  }

  static sendCreationToAcceptance = (
    id: string,
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string
  ): Promise<unknown> => {
    return (
      api.broadSign
        .sendCreationToAcceptance(id)
        .then(response => {
          const result = response.data?.sendBroadsignCreationToAcceptance

          if (!result?.success) {
            addNotification(
              NotifierType.ERROR,
              result?.errors?.length ? result.errors : t('broadSign.sendToAcceptancesFailed')
            )

            return
          }

          addNotification(NotifierType.SUCCESS, t('broadSign.sendToAcceptancesSuccessful'))
        })
        .catch(() => addNotification(NotifierType.ERROR, t('broadSign.sendToAcceptancesFailed')))
        // the acceptance statuses live on the buildings the tree and the responses are built from
        .finally(() =>
          Promise.all([
            BroadSignService.fetchCreationMediaTree(id),
            BroadSignService.fetchCreationAcceptances(id),
          ])
        )
    )
  }

  static acceptCreation = (
    id: string,
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string
  ): Promise<unknown> => {
    return api.broadSign
      .acceptCreation(id)
      .then(response => {
        if (response.data?.broadsignAcceptCreation.broadsignCreation.status !== 'accepted') {
          return
        }

        void store.dispatch(broadSignActions.setCreationStatus({ id, status: 'accepted' }))

        addNotification(NotifierType.SUCCESS, t('broadSign.creationAccepted'))
      })
      .catch(() => [])
  }

  static rejectCreation = (
    id: string,
    rerejectionReason: string,
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string,
    setModalOpen: (open: boolean) => void
  ): Promise<unknown> => {
    return api.broadSign.rejectCreation(id, rerejectionReason).then(response => {
      if (response.data?.broadsignRejectCreation.broadsignCreation.status !== 'rejected') {
        return
      }

      void store.dispatch(broadSignActions.setCreationStatus({ id, status: 'rejected' }))

      addNotification(NotifierType.SUCCESS, t('broadSign.creationRejected'))

      setModalOpen(false)
    })
  }

  static updateBroadsignCreation = (
    id: string,
    addNotification: (type: NotifierType, text: string | string[]) => void,
    t: (key: string) => string,
    filters: BroadSignFiltersModel,
    pageNumber: number,
    commercialSupervisorId?: string,
    departmentId?: string,
    advertiserId?: string
  ): Promise<unknown> => {
    return api.broadSign
      .updateBroadsignCreation(id, commercialSupervisorId, departmentId, advertiserId)
      .then(() => {
        addNotification(NotifierType.SUCCESS, t('broadSign.updateSuccessful'))
      })
      .then(() => {
        // Refresh the list after updating
        return BroadSignService.fetchBroadSignCreations(filters, pageNumber)
      })
      .catch(error => {
        console.error('Error updating BroadSign creation:', error)
        addNotification(NotifierType.ERROR, t('broadSign.updateFailed'))
      })
  }
}
